import { ITicker, enums } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import { getPriceForPair } from '../../helpers/statistics';
import { Direction } from '../../hooks';

export const PRICE_FACTOR = 10 ** 4;
export const GWEI = 10 ** -9;

/**
 * Handle state of limit order proxy
 */
export enum LimitTabs {
  Available,
  NotAvailable,
  Deploying,
  NotSupported,
}

export interface ILimitGrid {
  initPrice: number;
  finalPrice: number;
}

export interface ILimitValid {
  slippage: boolean;
  deadline: boolean;
  tradesCount: boolean;
}

export interface ILimitNativeToken {
  name: string;
  amount: number;
  loading: boolean;
  error: boolean;
}

interface ILimitState {
  limitTab: LimitTabs;
  baseBalance: number;
  quoteBalance: number;
  baseUSDBalance: string;
  quoteUSDBalance: string;
  currentPrice: number;
  stopPrice: number;
  direction: Direction;
  limitMode: enums.TriggerType;
  proxyWallet?: string;
  isGrid: boolean;
  grid: ILimitGrid;
  startDate: Date | null;
  endDate: Date | null;
  nativeToken: ILimitNativeToken;
  advancedSettings: boolean;
  valid: ILimitValid;
  loading: boolean;
}

export enum LimitActions {
  SetLimitTab = 'LIMIT_SET_TAB',
  SetBaseBalance = 'LIMIT_SET_BASE',
  SetQuoteBalance = 'LIMIT_SET_QUOTE',
  SetBaseUSDBalance = 'LIMIT_SET_BASE_USD',
  SetQuoteUSDBalance = 'LIMIT_SET_QUOTE_USD',
  SetCurrentPrice = 'LIMIT_SET_CURRENT_PRICE',
  SetStopPrice = 'LIMIT_SET_STOP_PRICE',
  SetDirection = 'LIMIT_SET_DIRECTION',
  SetLimitMode = 'LIMIT_SET_MODE',
  SetProxyWallet = 'LIMIT_SET_PROXY_WALLET',
  ToggleGrid = 'LIMIT_TOGGLE_GRID',
  SetGrid = 'LIMIT_SET_GRID',
  SetGridInitPrice = 'LIMIT_SET_GRID_INIT_PRICE',
  SetGridFinalPrice = 'LIMIT_SET_GRID_FINAL_PRICE',
  SetNativeToken = 'LIMIT_SET_NATIVE_TOKEN',
  ToggleAdvancedSettings = 'LIMIT_TOGGLE_ADVANCED_SETTINGS',
  SetValid = 'LIMIT_SET_VALID',
  SetStartDate = 'LIMIT_SET_START_DATE',
  SetEndDate = 'LIMIT_SET_END_DATE',
  SetLoading = 'LIMIT_SET_LOADING',
}

export const getInitialLimitState = ({
  baseAmount,
  quoteAmount,
  meta,
  flipped,
}: {
  baseAmount: BigNumber;
  quoteAmount: BigNumber;
  meta?: ITicker;
  flipped: boolean;
}): ILimitState => {
  const currentPrice = getPriceForPair({ ticker: meta, quotePrice: true });
  let stopPrice = currentPrice;

  if (baseAmount.isGreaterThan(0)) {
    if (!flipped) {
      stopPrice = quoteAmount.div(baseAmount).toNumber();
    } else {
      stopPrice = baseAmount.div(quoteAmount).toNumber();
    }
  }

  return {
    limitTab: LimitTabs.Available,
    baseBalance: 0,
    quoteBalance: 0,
    baseUSDBalance: '0.0',
    quoteUSDBalance: '0.0',
    currentPrice,
    stopPrice,
    direction: Direction.BaseQuote,
    limitMode: enums.TriggerType.Overline,
    isGrid: false,
    grid: {
      initPrice: currentPrice,
      finalPrice: currentPrice,
    },
    startDate: new Date(),
    endDate: null,
    nativeToken: {
      name: '',
      amount: 0,
      loading: true,
      error: false,
    },
    advancedSettings: false,
    valid: {
      slippage: true,
      deadline: true,
      tradesCount: true,
    },
    loading: false,
  };
};

export const limitReducer = (
  state: ILimitState,
  action: { payload?: any; type: LimitActions }
): ILimitState => {
  switch (action.type) {
  case LimitActions.SetLimitTab:
    return {
      ...state,
      limitTab: action.payload,
    };
  case LimitActions.SetBaseBalance:
    return {
      ...state,
      baseBalance: action.payload,
    };
  case LimitActions.SetQuoteBalance:
    return {
      ...state,
      quoteBalance: action.payload,
    };
  case LimitActions.SetBaseUSDBalance:
    return {
      ...state,
      baseUSDBalance: action.payload,
    };
  case LimitActions.SetQuoteUSDBalance:
    return {
      ...state,
      quoteUSDBalance: action.payload,
    };
  case LimitActions.SetCurrentPrice:
    return {
      ...state,
      currentPrice: action.payload,
    };
  case LimitActions.SetStopPrice:
    return {
      ...state,
      stopPrice: action.payload,
    };
  case LimitActions.SetDirection:
    return {
      ...state,
      direction: action.payload,
    };
  case LimitActions.SetLimitMode:
    return {
      ...state,
      limitMode: action.payload,
    };
  case LimitActions.SetProxyWallet:
    return {
      ...state,
      proxyWallet: action.payload,
    };
  case LimitActions.ToggleGrid:
    return {
      ...state,
      isGrid: !state.isGrid,
    };
  case LimitActions.SetGrid:
    return {
      ...state,
      grid: action.payload,
    };
  case LimitActions.SetGridInitPrice:
    return {
      ...state,
      grid: {
        ...state.grid,
        initPrice: action.payload,
      },
    };
  case LimitActions.SetGridFinalPrice:
    return {
      ...state,
      grid: {
        ...state.grid,
        finalPrice: action.payload,
      },
    };
  case LimitActions.SetNativeToken:
    return {
      ...state,
      nativeToken: {
        ...state.nativeToken,
        ...action.payload,
      },
    };
  case LimitActions.ToggleAdvancedSettings:
    return {
      ...state,
      advancedSettings: !state.advancedSettings,
    };
  case LimitActions.SetValid:
    return {
      ...state,
      valid: {
        ...state.valid,
        ...action.payload,
      },
    };
  case LimitActions.SetStartDate:
    return {
      ...state,
      startDate: action.payload,
    };
  case LimitActions.SetEndDate:
    return {
      ...state,
      endDate: action.payload,
    };
  case LimitActions.SetLoading:
    return {
      ...state,
      loading: action.payload,
    };
  default:
    return state;
  }
};
